import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import type { GECRoomStatus } from './GenderEqualityCuisine';

interface Props {
  roomId: string;
  status: GECRoomStatus;
  isAdmin: boolean;
  refreshStatus: () => void;
}

interface DishForm {
  dishName: string;
  realName: string;
  allergy: string;
  identifyingDetails: string;
  description: string;
}

const emptyForm: DishForm = {
  dishName: '',
  realName: '',
  allergy: '',
  identifyingDetails: '',
  description: '',
};

function WaitingStage({ roomId, status, isAdmin, refreshStatus }: Props) {
  const [savingRole, setSavingRole] = useState<'chef' | 'voter' | null>(null);
  const [roleError, setRoleError] = useState('');

  const [form, setForm] = useState<DishForm>(emptyForm);
  const [submittingDish, setSubmittingDish] = useState(false);
  const [dishError, setDishError] = useState('');
  const [loadedExisting, setLoadedExisting] = useState(false);

  // Prefill form from server if the user already submitted a dish (revisit)
  useEffect(() => {
    const loadExisting = async () => {
      if (status.currentUserRole !== 'chef' || loadedExisting) return;
      if (!status.currentUserDishSubmitted) {
        setLoadedExisting(true);
        return;
      }
      const result = await api.gecGetDishes(roomId);
      if (result.success) {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const myId = userData.userId || userData.user_id;
        const mine = (result.data.dishes || []).find((d: any) => d.userId === myId);
        if (mine) {
          setForm({
            dishName: mine.dishName || '',
            realName: mine.realName || '',
            allergy: mine.allergy || '',
            identifyingDetails: mine.identifyingDetails || '',
            description: mine.description || '',
          });
        }
      }
      setLoadedExisting(true);
    };
    loadExisting();
  }, [roomId, status.currentUserRole, status.currentUserDishSubmitted, loadedExisting]);

  const pickRole = async (role: 'chef' | 'voter') => {
    setRoleError('');
    setSavingRole(role);
    const result = await api.gecSetRole(roomId, role);
    setSavingRole(null);
    if (result.success) {
      if (role === 'voter') setForm(emptyForm);
      refreshStatus();
    } else {
      setRoleError(result.error || 'Failed to set role');
    }
  };

  const handleSubmitDish = async (e: React.FormEvent) => {
    e.preventDefault();
    setDishError('');
    for (const [key, label] of [
      ['dishName', 'dish name'],
      ['realName', 'your real name'],
      ['identifyingDetails', 'identifying details'],
      ['description', 'description'],
    ] as const) {
      if (!form[key].trim()) {
        setDishError(`Please fill in ${label}.`);
        return;
      }
    }
    setSubmittingDish(true);
    const result = await api.gecSubmitDish(roomId, form);
    setSubmittingDish(false);
    if (result.success) {
      refreshStatus();
    } else {
      setDishError(result.error || 'Failed to submit dish');
    }
  };

  const myRole = status.currentUserRole;

  return (
    <div className="game-content gec-content">
      <div className="gec-status-banner">
        <span>📋 {status.dishesSubmitted} / {status.totalChefs} dishes submitted</span>
        {isAdmin && (
          <span className="gec-admin-tag">You're the admin — press Start Game above when everyone's ready.</span>
        )}
        {!isAdmin && (
          <span className="gec-waiting-tag">⏳ Waiting for admin to start the game…</span>
        )}
      </div>

      <h3 className="gec-section-title">Pick your role</h3>
      <div className="gec-role-picker">
        <button
          type="button"
          className={`gec-role-card ${myRole === 'chef' ? 'selected' : ''}`}
          disabled={savingRole !== null}
          onClick={() => pickRole('chef')}
        >
          <div className="gec-role-emoji">🧑‍🍳</div>
          <div className="gec-role-label">Chef</div>
          <div className="gec-role-hint">I'm bringing a dish</div>
        </button>
        <button
          type="button"
          className={`gec-role-card ${myRole === 'voter' ? 'selected' : ''}`}
          disabled={savingRole !== null}
          onClick={() => pickRole('voter')}
        >
          <div className="gec-role-emoji">🗳️</div>
          <div className="gec-role-label">Voter</div>
          <div className="gec-role-hint">I'm here to taste &amp; vote</div>
        </button>
      </div>
      {roleError && <div className="gec-error">{roleError}</div>}

      {myRole === 'chef' && (
        <div className="gec-chef-form">
          <h3 className="gec-section-title">Tell us about your dish</h3>
          {status.currentUserDishSubmitted && (
            <div className="gec-success-banner">
              ✅ Submitted! You can keep editing until the admin starts the game.
            </div>
          )}
          <form onSubmit={handleSubmitDish}>
            <label className="gec-label">
              Dish name
              <input
                type="text"
                value={form.dishName}
                onChange={(e) => setForm({ ...form, dishName: e.target.value })}
                maxLength={80}
                placeholder="e.g. Spicy Mango Curry"
              />
            </label>
            <label className="gec-label">
              Your real name <span className="gec-hint">(hidden from voters until the reveal)</span>
              <input
                type="text"
                value={form.realName}
                onChange={(e) => setForm({ ...form, realName: e.target.value })}
                maxLength={80}
                placeholder="e.g. Anna Smith"
              />
            </label>
            <label className="gec-label">
              Food allergies in this dish
              <input
                type="text"
                value={form.allergy}
                onChange={(e) => setForm({ ...form, allergy: e.target.value })}
                maxLength={300}
                placeholder="e.g. peanuts, dairy — or leave blank"
              />
            </label>
            <label className="gec-label">
              Identifying details for voters
              <span className="gec-hint">(main ingredients, cooking method, plate color, etc.)</span>
              <textarea
                value={form.identifyingDetails}
                onChange={(e) => setForm({ ...form, identifyingDetails: e.target.value })}
                maxLength={500}
                placeholder="e.g. served on a white plate with a side of jasmine rice; chicken-based, slow cooked"
                rows={3}
              />
            </label>
            <label className="gec-label">
              Description (be creative &amp; fun!)
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                maxLength={1000}
                placeholder="Tell voters about your dish, the inspiration, the vibe…"
                rows={5}
              />
            </label>
            {dishError && <div className="gec-error">{dishError}</div>}
            <button
              type="submit"
              className="gec-primary-btn"
              disabled={submittingDish}
            >
              {submittingDish ? 'Saving…' : status.currentUserDishSubmitted ? 'Update Dish' : 'Submit Dish'}
            </button>
          </form>
        </div>
      )}

      {myRole === 'voter' && (
        <div className="gec-info-box">
          🗳️ You're a voter! When the admin starts the game, you'll get 5 tokens to spend across the dishes.
        </div>
      )}
    </div>
  );
}

export default WaitingStage;
